import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { formatLicenseDateForDisplay } from "@/lib/licenseDates";
import {
  hasActiveCommercialLicense,
  moduleLabel,
  resolveActiveModuleIds,
  resolveCommercialModuleCatalog,
} from "@/lib/commercialLicenseRequests";
import { CONFIGURACION_DESPACHO_PATH } from "@/modules/config/configuracionRoutes";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import BuyerProfileSummary from "@/components/BuyerProfileSummary";

/**
 * Resumen compacto titular / trial para vista enfocada de solicitud de licencia.
 */
export default function LicenseCommercialSummary({
  systemInfo,
  profile = {},
  showEditTitular = true,
}) {
  const sub = systemInfo?.subscription || {};
  const licensed = hasActiveCommercialLicense(systemInfo);
  const catalog = resolveCommercialModuleCatalog(systemInfo);
  const activeModuleIds = licensed ? resolveActiveModuleIds(systemInfo) : [];
  const authUsers = systemInfo?.license?.authorized_users ?? systemInfo?.license?.payload?.authorized_users;
  const authClients = systemInfo?.license?.authorized_clients ?? systemInfo?.license?.payload?.authorized_clients;
  const trialDays = Number(systemInfo?.trial_days) > 0 ? Number(systemInfo.trial_days) : 3;
  const inTrial = sub.mode === "trial";
  const trialActive = inTrial && sub.ok;
  const trialEnds = sub.trial_ends_at ? String(sub.trial_ends_at).slice(0, 10) : "";

  let estadoLabel = "—";
  if (trialActive) estadoLabel = "Prueba activa";
  else if (inTrial && !sub.ok) estadoLabel = "Prueba vencida";
  else if (sub.mode === "licensed" && sub.ok) estadoLabel = "Licencia activa";
  else if (!sub.ok) estadoLabel = "Requiere licencia";

  const titular =
    (profile.razon_social || "").trim() ||
    (profile.nombre_comercial || "").trim() ||
    (profile.responsable || "").trim() ||
    "—";
  const rfc = (profile.rfc || "").trim().toUpperCase() || "—";

  let vigencia = "—";
  if (trialActive && trialEnds) {
    vigencia = `Prueba hasta ${formatLicenseDateForDisplay(trialEnds)} (${trialDays} días)`;
  } else if (sub.mode === "licensed" && systemInfo?.license?.payload?.valid_to) {
    vigencia = formatLicenseDateForDisplay(String(systemInfo.license.payload.valid_to).slice(0, 10));
  }

  return (
    <div
      className="rounded-sm border border-slate-200 bg-white p-4 space-y-3"
      data-testid="license-commercial-summary"
    >
      <div>
        <h3 className="text-sm font-semibold text-slate-900 m-0">Resumen de tu instalación</h3>
        <p className="text-[11px] text-slate-600 mt-1 mb-0">
          Datos de referencia para tu solicitud. No necesitas repetir el registro completo.
        </p>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-[11px] m-0">
        <div>
          <dt className="text-slate-500 uppercase tracking-wide text-[10px]">Titular / razón social</dt>
          <dd className="text-slate-900 font-medium mt-0.5 m-0 break-words">{titular}</dd>
        </div>
        <div>
          <dt className="text-slate-500 uppercase tracking-wide text-[10px]">RFC</dt>
          <dd className="font-mono text-slate-900 mt-0.5 m-0">{rfc}</dd>
        </div>
        <div>
          <dt className="text-slate-500 uppercase tracking-wide text-[10px]">Estado</dt>
          <dd
            className={`mt-0.5 m-0 font-medium ${trialActive ? "text-emerald-800" : "text-slate-900"}`}
          >
            {estadoLabel}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500 uppercase tracking-wide text-[10px]">Vigencia</dt>
          <dd className="text-slate-900 mt-0.5 m-0">{vigencia}</dd>
        </div>
        <div className="sm:col-span-2 lg:col-span-1">
          <dt className="text-slate-500 uppercase tracking-wide text-[10px]">ID instalación</dt>
          <dd className="font-mono text-slate-800 mt-0.5 m-0 break-all">
            {systemInfo?.installation_id || "—"}
          </dd>
        </div>
      </dl>

      {licensed && activeModuleIds.length > 0 ? (
        <div className="text-[11px] text-slate-700 border-t border-slate-100 pt-2" data-testid="license-commercial-summary-modules">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide m-0 mb-1">Módulos autorizados</p>
          <p className="m-0">{activeModuleIds.map((id) => moduleLabel(id, catalog)).join(", ")}</p>
          {(authUsers || authClients) ? (
            <p className="text-[10px] text-slate-500 mt-1 m-0">
              {authUsers ? `${authUsers} usuarios autorizados` : null}
              {authUsers && authClients ? " · " : null}
              {authClients ? `${authClients} clientes autorizados` : null}
            </p>
          ) : null}
        </div>
      ) : null}

      {showEditTitular ? (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs text-[#0F2D5C] justify-start"
              data-testid="license-commercial-edit-titular-toggle"
            >
              Editar datos del titular
              <ChevronDown className="w-3.5 h-3.5 ml-1" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2 space-y-2" data-testid="license-commercial-edit-titular">
            <BuyerProfileSummary profile={profile} systemInfo={systemInfo} />
            <p className="text-[10px] text-slate-600 m-0">
              Para modificar razón social, RFC o contacto, abre{" "}
              <Link to={CONFIGURACION_DESPACHO_PATH} className="font-semibold text-[#0F2D5C] underline">
                Ver datos completos del negocio
              </Link>
              .
            </p>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}
